import React, { useState, useMemo } from 'react';
import { X, ChevronLeft, ChevronRight, Maximize2, Download, Image, Star, Loader2, Trash2 } from 'lucide-react';
import JSZip from 'jszip';
import ImageLabel from './ImageLabel';

interface ImageGalleryModalProps {
  imageGroups: IImageGroup[];
  currentHistoryIndex: number;
  onClose: () => void;
  onNavigateToImage: (index: number, imageIndexInGroup?: number) => void;
  onDownload: (imageUrl: string, groupIndex: number, imageIndexInGroup?: number) => void;
  onToggleStar: (image: IImage) => void;
  onDeletePicture: (image: IImage) => Promise<void>;
}

const ImageGalleryModal: React.FC<ImageGalleryModalProps> = ({
  imageGroups,
  currentHistoryIndex,
  onClose,
  onNavigateToImage,
  onDownload,
  onToggleStar,
  onDeletePicture,
}) => {
  const [selectedImageForPreview, setSelectedImageForPreview] = useState<{
    groupIndex: number;
    imageIndexInGroup: number;
    image: IImage;
  } | null>(null);
  const [activeTab, setActiveTab] = useState<'all' | 'starred'>('all');
  const [isDownloadingZip, setIsDownloadingZip] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<IImage | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Flatten all images with their group indices for easy navigation
  const allImages = useMemo(() => {
    const flattened: Array<{
      groupIndex: number;
      imageIndexInGroup: number;
      image: IImage;
      isMultiImage: boolean;
    }> = [];

    imageGroups.forEach((group, groupIndex) => {
      group.images.forEach((image, imageIndexInGroup) => {
        flattened.push({
          groupIndex,
          imageIndexInGroup,
          image,
          isMultiImage: group.images.length > 1,
        });
      });
    });

    return flattened;
  }, [imageGroups]);

  // Starred images across the entire thread (used for badge count and Download All)
  const starredImages = useMemo(
    () => allImages.filter((item) => item.image.starred === true),
    [allImages]
  );

  // Images shown in the current tab — used by both the grid and lightbox navigation
  const displayedImages = useMemo(
    () => activeTab === 'starred' ? starredImages : allImages,
    [activeTab, starredImages, allImages]
  );

  const downloadAllStarred = async () => {
    const toDownload = starredImages.filter((item) => item.image.output_url !== '');
    if (toDownload.length === 0) return;

    setIsDownloadingZip(true);
    try {
      const zip = new JSZip();
      const results = await Promise.allSettled(
        toDownload.map(async (item) => {
          const response = await fetch(item.image.output_url);
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          const blob = await response.blob();
          zip.file(`${item.image.picture_id}.png`, blob);
        })
      );

      // Log skipped images
      const skipped = results
        .map((r, i) => (r.status === 'rejected' ? toDownload[i].image.picture_id : null))
        .filter(Boolean);
      if (skipped.length > 0) {
        console.warn('Starred image download: skipped picture_ids due to fetch failure:', skipped);
      }

      const content = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(content);
      const a = document.createElement('a');
      a.href = url;
      a.download = `starred-images-${Date.now()}.zip`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setIsDownloadingZip(false);
    }
  };

  const handleImageClick = (groupIndex: number, imageIndexInGroup?: number) => {
    onNavigateToImage(groupIndex, imageIndexInGroup);
    onClose();
  };

  const handlePreviewClick = (groupIndex: number, imageIndexInGroup: number, image: IImage) => {
    setSelectedImageForPreview({ groupIndex, imageIndexInGroup, image });
  };

  const handlePreviewNavigation = (direction: 'prev' | 'next') => {
    if (!selectedImageForPreview) return;

    const currentIndex = displayedImages.findIndex(
      item => item.groupIndex === selectedImageForPreview.groupIndex &&
              item.imageIndexInGroup === selectedImageForPreview.imageIndexInGroup
    );

    if (currentIndex === -1) return;

    const newIndex = direction === 'prev'
      ? Math.max(0, currentIndex - 1)
      : Math.min(displayedImages.length - 1, currentIndex + 1);

    const newItem = displayedImages[newIndex];
    setSelectedImageForPreview({
      groupIndex: newItem.groupIndex,
      imageIndexInGroup: newItem.imageIndexInGroup,
      image: newItem.image,
    });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    setDeleteError(null);
    try {
      await onDeletePicture(deleteTarget);
      // If we were previewing the deleted image, close preview or navigate away
      if (
        selectedImageForPreview &&
        selectedImageForPreview.image.picture_id === deleteTarget.picture_id
      ) {
        const currentIndex = displayedImages.findIndex(
          item => item.image.picture_id === deleteTarget.picture_id
        );
        // Try to go to previous, then next, then close preview
        if (currentIndex > 0) {
          const prev = displayedImages[currentIndex - 1];
          setSelectedImageForPreview({
            groupIndex: prev.groupIndex,
            imageIndexInGroup: prev.imageIndexInGroup,
            image: prev.image,
          });
        } else if (currentIndex < displayedImages.length - 1) {
          const next = displayedImages[currentIndex + 1];
          setSelectedImageForPreview({
            groupIndex: next.groupIndex,
            imageIndexInGroup: next.imageIndexInGroup,
            image: next.image,
          });
        } else {
          setSelectedImageForPreview(null);
        }
      }
      setDeleteTarget(null);
    } catch (err) {
      console.error('Failed to delete image:', err);
      setDeleteError('Failed to delete image. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const confirmDialog = deleteTarget && (
    <div className="fixed inset-0 bg-black/70 z-[60] flex items-center justify-center p-4 animate-fade-in"
      onClick={() => !isDeleting && setDeleteTarget(null)}
    >
      <div className="bg-gray-800 border border-gray-600 rounded-xl p-6 max-w-sm w-full shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-lg font-bold text-white mb-2">Delete image?</h3>
        <p className="text-gray-300 text-sm mb-6">This action cannot be undone.</p>
        {deleteError && (
          <p className="text-red-400 text-sm mb-4">{deleteError}</p>
        )}
        <div className="flex justify-end gap-3">
          <button
            onClick={() => setDeleteTarget(null)}
            disabled={isDeleting}
            className="px-4 py-2 text-sm font-semibold text-gray-300 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleDeleteConfirm}
            disabled={isDeleting}
            className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-red-600 hover:bg-red-500 disabled:opacity-50 rounded-lg transition-colors"
          >
            {isDeleting && <Loader2 className="w-4 h-4 animate-spin" />}
            Delete
          </button>
        </div>
      </div>
    </div>
  );

  // Preview Modal
  if (selectedImageForPreview) {
    const currentIndex = displayedImages.findIndex(
      item => item.groupIndex === selectedImageForPreview.groupIndex &&
              item.imageIndexInGroup === selectedImageForPreview.imageIndexInGroup
    );
    const canGoPrev = currentIndex > 0;
    const canGoNext = currentIndex < displayedImages.length - 1;

    // Use the live image from displayedImages so starred state stays in sync with Firestore updates
    const liveImage = currentIndex !== -1 ? displayedImages[currentIndex].image : selectedImageForPreview.image;

    return (
      <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4 animate-fade-in">
        {/* Close button */}
        <button
          onClick={() => setSelectedImageForPreview(null)}
          className="absolute top-4 right-4 p-3 bg-gray-800/80 hover:bg-gray-700/80 backdrop-blur-sm text-white rounded-full transition-all duration-200 hover:scale-110 active:scale-95 shadow-xl group z-50"
          aria-label="Close preview"
        >
          <X className="w-6 h-6 group-hover:rotate-90 transition-transform duration-200" />
        </button>

        {/* Navigation arrows */}
        {canGoPrev && (
          <button
            onClick={() => handlePreviewNavigation('prev')}
            className="absolute left-4 top-1/2 -translate-y-1/2 p-3 bg-gray-800/80 hover:bg-gray-700/80 backdrop-blur-sm text-white rounded-full transition-all duration-200 hover:scale-110 active:scale-95 shadow-xl z-50"
            aria-label="Previous image"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
        )}

        {canGoNext && (
          <button
            onClick={() => handlePreviewNavigation('next')}
            className="absolute right-4 top-1/2 -translate-y-1/2 p-3 bg-gray-800/80 hover:bg-gray-700/80 backdrop-blur-sm text-white rounded-full transition-all duration-200 hover:scale-110 active:scale-95 shadow-xl z-50"
            aria-label="Next image"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        )}

        {/* Image counter */}
        <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-gray-800/90 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-semibold shadow-xl z-50">
          {currentIndex + 1} / {displayedImages.length}
        </div>

        {/* Action buttons */}
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-50">
          <button
            onClick={() => onToggleStar(liveImage)}
            className="flex items-center gap-2 bg-gray-700/90 hover:bg-gray-700 backdrop-blur-sm text-white px-4 py-2 rounded-lg font-semibold transition-all duration-200 hover:scale-105 active:scale-95 shadow-xl"
          >
            <Star className={`w-4 h-4 ${liveImage.starred ? 'fill-yellow-400 text-yellow-400' : ''}`} />
            {liveImage.starred ? 'Unstar' : 'Star'}
          </button>
          <button
            onClick={() => handleImageClick(selectedImageForPreview.groupIndex, selectedImageForPreview.imageIndexInGroup)}
            className="flex items-center gap-2 bg-purple-600/90 hover:bg-purple-600 backdrop-blur-sm text-white px-4 py-2 rounded-lg font-semibold transition-all duration-200 hover:scale-105 active:scale-95 shadow-xl"
          >
            <Maximize2 className="w-4 h-4" />
            Go to Image
          </button>
          <button
            onClick={() => onDownload(liveImage.output_url, selectedImageForPreview.groupIndex, selectedImageForPreview.imageIndexInGroup)}
            className="flex items-center gap-2 bg-green-600/90 hover:bg-green-600 backdrop-blur-sm text-white px-4 py-2 rounded-lg font-semibold transition-all duration-200 hover:scale-105 active:scale-95 shadow-xl"
          >
            <Download className="w-4 h-4" />
            Download
          </button>
          <button
            onClick={() => setDeleteTarget(liveImage)}
            className="flex items-center gap-2 bg-red-600/90 hover:bg-red-600 backdrop-blur-sm text-white px-4 py-2 rounded-lg font-semibold transition-all duration-200 hover:scale-105 active:scale-95 shadow-xl"
          >
            <Trash2 className="w-4 h-4" />
            Delete
          </button>
        </div>

        {/* Large preview image */}
        <img
          src={liveImage.output_url}
          alt="Preview"
          className="max-w-[90%] max-h-[90%] object-contain rounded-xl shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        />

        {/* Confirmation dialog */}
        {confirmDialog}
      </div>
    );
  }

  // Main Gallery Modal
  return (
    <div
      className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-6 animate-fade-in"
      onClick={onClose}
    >
      <div
        className="bg-gray-900/95 backdrop-blur-xl border border-gray-700/50 rounded-2xl shadow-2xl max-w-6xl w-full max-h-[90vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-700/50">
          <div>
            <h2 className="text-2xl font-bold text-white flex items-center gap-3">
              <div className="p-2 bg-gradient-to-br from-purple-600 to-purple-500 rounded-lg">
                <Image className="w-6 h-6" />
              </div>
              Image Gallery
            </h2>
            <p className="text-sm text-gray-400 mt-1">
              {allImages.length} {allImages.length === 1 ? 'image' : 'images'} generated in this thread
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 bg-gray-800/60 hover:bg-gray-700/60 border border-gray-700/50 text-gray-300 hover:text-white rounded-lg transition-all duration-200 hover:scale-110 active:scale-95 group"
            aria-label="Close gallery"
          >
            <X className="w-5 h-5 group-hover:rotate-90 transition-transform duration-200" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 px-6 pt-2 border-b border-gray-700/50">
          <button
            onClick={() => setActiveTab('all')}
            className={`px-4 py-2 text-sm font-semibold rounded-t-lg transition-colors ${
              activeTab === 'all'
                ? 'bg-gray-800 text-white border-b-2 border-purple-500'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            All Images
          </button>
          <button
            onClick={() => setActiveTab('starred')}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-t-lg transition-colors ${
              activeTab === 'starred'
                ? 'bg-gray-800 text-white border-b-2 border-yellow-400'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            <Star className={`w-3.5 h-3.5 ${activeTab === 'starred' ? 'fill-yellow-400 text-yellow-400' : ''}`} />
            Starred
            {starredImages.length > 0 && (
              <span className="bg-yellow-400 text-black text-xs font-bold px-1.5 py-0.5 rounded-full">
                {starredImages.length}
              </span>
            )}
          </button>
        </div>

        {/* Starred tab: Download All bar */}
        {activeTab === 'starred' && (
          <div className="flex items-center justify-between px-6 py-3 border-b border-gray-700/30">
            <p className="text-sm text-gray-400">
              {starredImages.length === 0
                ? 'No starred images yet'
                : `${starredImages.length} starred ${starredImages.length === 1 ? 'image' : 'images'}`}
            </p>
            <button
              onClick={downloadAllStarred}
              disabled={starredImages.length === 0 || isDownloadingZip}
              className="flex items-center gap-2 bg-yellow-500 hover:bg-yellow-400 disabled:bg-gray-700 disabled:text-gray-500 disabled:cursor-not-allowed text-black font-semibold px-4 py-2 rounded-lg text-sm transition-colors"
            >
              {isDownloadingZip ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Download className="w-4 h-4" />
              )}
              {isDownloadingZip ? 'Preparing…' : 'Download All'}
            </button>
          </div>
        )}

        {/* Image Grid */}
        <div className="flex-1 overflow-y-auto p-6">
          {displayedImages.length === 0 && activeTab === 'starred' ? (
            <div className="flex flex-col items-center justify-center h-64 text-gray-400">
              <Star className="w-16 h-16 mb-4 opacity-30" />
              <p className="text-lg font-medium">No starred images yet</p>
              <p className="text-sm">Click ★ on any image to star it</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {displayedImages.map((item, index) => {
                const isCurrentImage = item.groupIndex === currentHistoryIndex;
                return (
                  <div
                    key={`${item.groupIndex}-${item.imageIndexInGroup}-${item.image.picture_id}`}
                    className={`group relative aspect-square rounded-lg overflow-hidden cursor-pointer transition-all duration-200 hover:scale-105 hover:shadow-xl ${
                      isCurrentImage
                        ? 'ring-4 ring-blue-500 ring-offset-2 ring-offset-gray-900 shadow-xl shadow-blue-500/50'
                        : 'hover:ring-2 hover:ring-purple-500/50'
                    }`}
                  >
                    {/* Image */}
                    <img
                      src={item.image.thumb_url || item.image.output_url}
                      alt={`Generated image ${index + 1}`}
                      className="w-full h-full object-cover"
                      onClick={() => handlePreviewClick(item.groupIndex, item.imageIndexInGroup, item.image)}
                    />

                    {item.image.meta?.label && (
                      <ImageLabel
                        label={item.image.meta.label}
                        platformLabel={item.image.meta.platformLabel || ''}
                        aspectRatio={item.image.meta.aspectRatio || ''}
                      />
                    )}

                    {/* Star button — always visible */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onToggleStar(item.image);
                      }}
                      className="absolute top-2 left-2 z-10 p-1 rounded-full bg-black/50 backdrop-blur-sm transition-colors hover:bg-black/70"
                      aria-label={item.image.starred ? 'Unstar image' : 'Star image'}
                      title={item.image.starred ? 'Remove star' : 'Star this image'}
                    >
                      <Star
                        className={`w-4 h-4 ${item.image.starred ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`}
                      />
                    </button>

                    {/* Overlay with actions */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex flex-col justify-end p-3">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-white text-xs font-semibold bg-black/50 px-2 py-1 rounded">
                          #{index + 1}
                        </span>
                        <div className="flex gap-1">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleImageClick(item.groupIndex, item.imageIndexInGroup);
                            }}
                            className="p-1.5 bg-purple-600/90 hover:bg-purple-600 text-white rounded transition-colors"
                            aria-label="Go to image"
                            title="Go to image"
                          >
                            <Maximize2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onDownload(item.image.output_url, item.groupIndex, item.imageIndexInGroup);
                            }}
                            className="p-1.5 bg-green-600/90 hover:bg-green-600 text-white rounded transition-colors"
                            aria-label="Download image"
                            title="Download"
                          >
                            <Download className="w-4 h-4" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeleteTarget(item.image);
                            }}
                            className="p-1.5 bg-red-600/90 hover:bg-red-600 text-white rounded transition-colors"
                            aria-label="Delete image"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Multi-image indicator */}
                    {item.isMultiImage && (
                      <div className="absolute top-2 right-2 bg-blue-500/90 backdrop-blur-sm text-white px-2 py-1 rounded text-xs font-semibold">
                        Batch
                      </div>
                    )}

                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Confirmation dialog */}
        {confirmDialog}
      </div>
    </div>
  );
};

export default ImageGalleryModal;
